import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { UsersApiService } from '@shared/services/users-api.service';
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { corporateKeyMsg } from "@shared/validators/custom-form-validators-messages";
import { firstValueFrom, Observable } from 'rxjs';
import { Company } from '@models/companies/company';
import { SelectOption } from "@models/ui-models/select-options";
import { UserRecord } from '@models/organisations/user-record';

@Component({
  selector: 'app-company-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    DatePickerComponent,
    DividerComponent,
    SelectComponent,
    ButtonComponent
  ],
  templateUrl: './company-edit-modal.component.html',
  styleUrl: './company-edit-modal.component.scss'
})
export class CompanyEditModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private usersService = inject(UsersApiService);
  private fb: FormBuilder = inject(FormBuilder);

  @Input() public editModel!: Company;
  @Input() public $confirm!: () => Observable<unknown>;

  protected readonly corporateKeyMsg = corporateKeyMsg;

  form!: FormGroup;
  users: UserRecord[] = [];
  usersOption: SelectOption[] = [];
  saving = false;

  async ngOnInit(): Promise<void> {
    this.activeModal.update({ size: 'lg' });
    this.createUserOptions();
    this.initForm();
  }

  private async createUserOptions(): Promise<void> {
    this.users = await firstValueFrom(this.usersService.getAssignableUsersCached());
    this.usersOption = this.users.map(user => {
      return {
        value: user.id,
        label: user.fullName
      };
    })
  }

  private initForm(): void {
    this.form = this.fb.group({
      corporateKey: [this.editModel.corporateKey, [CustomFormValidators.CorporateKeyValidator]],
      nextAnnualReviewDate: [this.editModel.nextAnnualReviewDate],
      partnerManagerId: [this.editModel.partnerManagerId],
      accountManagerId: [this.editModel.accountManagerId],
      referenceNumber: [this.editModel.referenceNumber],
      familyGroup: [this.editModel.familyGroup],
    });
  }

  private updateModel(): void {
    for (const key in this.form.getRawValue()) {
      this.editModel[key] = (this.form.getRawValue() as Partial<Company>)[key];
    }
  }

  close(): void {
    this.activeModal.close();
  }

  $submit(): void {
    this.updateModel();

    this.saving = true;
    this.$confirm().subscribe({
      complete: () => this.close(),
      error: () => this.saving = false
     });
  }
}
